/**
 * @module game/PowerUpManager
 * Tracks which power-ups are currently active and how much time remains.
 *
 * Power-up effects:
 *  - freeze       — all fruit physics runs at CONFIG.powerup.freezeVelocityScale speed
 *  - double       — all scored fruit awards ×2 points
 *  - magnet       — fruit within CONFIG.powerup.magnetRadius are pulled toward the pointer
 *
 * Durations are driven by CONFIG.powerup so designers tune without touching code.
 */

import { CONFIG } from '../game-config.js';
import type { PowerUpType } from '../game-config.js';

/** Read-only view of the current power-up effect state. */
export interface PowerUpState {
  /** Whether freeze (slow-motion physics) is currently active. */
  freezeActive: boolean;
  /** Whether double-points is currently active. */
  doubleActive: boolean;
  /** Whether magnet is currently active. */
  magnetActive: boolean;
  /** Remaining freeze duration (ms). 0 when inactive. */
  freezeRemaining: number;
  /** Remaining double-points duration (ms). 0 when inactive. */
  doubleRemaining: number;
  /** Remaining magnet duration (ms). 0 when inactive. */
  magnetRemaining: number;
}

/**
 * Manages timed power-up activations.
 *
 * Call {@link PowerUpManager.activate} when a power-up entity is slashed.
 * Call {@link PowerUpManager.tick} each game frame with the elapsed delta.
 * Read {@link PowerUpManager.state} for the current effect flags.
 *
 * One instance per game session; call {@link PowerUpManager.reset} on restart.
 */
export class PowerUpManager {
  private _freeze = 0;   // remaining ms
  private _double = 0;   // remaining ms
  private _magnet = 0;   // remaining ms

  /**
   * Activates the given power-up, resetting its timer to the full duration.
   * Activating an already-active power-up restarts its timer (not additive).
   */
  activate(type: PowerUpType): void {
    switch (type) {
      case 'freeze':
        this._freeze = CONFIG.powerup.freezeDuration;
        break;
      case 'double':
        this._double = CONFIG.powerup.doublePointsDuration;
        break;
      case 'magnet':
        this._magnet = CONFIG.powerup.magnetDuration;
        break;
    }
  }

  /**
   * Advances all active power-up timers by `deltaMs`.
   * Timers floor at 0; they never go negative.
   *
   * @param deltaMs - Frame delta in milliseconds.
   */
  tick(deltaMs: number): void {
    this._freeze = Math.max(0, this._freeze - deltaMs);
    this._double = Math.max(0, this._double - deltaMs);
    this._magnet = Math.max(0, this._magnet - deltaMs);
  }

  /** Whether freeze is currently active. */
  get freezeActive(): boolean {
    return this._freeze > 0;
  }

  /** Whether double-points is currently active. */
  get doubleActive(): boolean {
    return this._double > 0;
  }

  /** Whether magnet is currently active. */
  get magnetActive(): boolean {
    return this._magnet > 0;
  }

  /**
   * Velocity scale to apply to all physics bodies.
   * Returns CONFIG.powerup.freezeVelocityScale when freeze is active, else 1.
   */
  get velocityScale(): number {
    return this._freeze > 0 ? CONFIG.powerup.freezeVelocityScale : 1;
  }

  /** Read-only snapshot — passed to HUD and render layer. */
  get state(): PowerUpState {
    return {
      freezeActive: this._freeze > 0,
      doubleActive: this._double > 0,
      magnetActive: this._magnet > 0,
      freezeRemaining: this._freeze,
      doubleRemaining: this._double,
      magnetRemaining: this._magnet,
    };
  }

  /** Resets all active power-ups for a new game session. */
  reset(): void {
    this._freeze = 0;
    this._double = 0;
    this._magnet = 0;
  }
}
